'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { useSession } from '@lib/auth/client';
import { ListingsListAllFavoritedDocument, ListingFavoriteSortOptions } from '@gql';
interface UseFavoritedListingIdsResult {
  /** Set of listing IDs that are favorited by the current user */
  favoritedIds: Set<string>;
  /** Whether the favorites are currently being loaded */
  isLoading: boolean;
  /** Check if a specific listing is favorited */
  isFavorited: (listingId: string) => boolean;
  /** Manually update the favorited state for a listing (for optimistic updates after mutations) */
  setFavorited: (listingId: string, favorited: boolean) => void;
}

/**
 * Hook to fetch the current user's favorited listing IDs on the client.
 * Only fetches when the user is authenticated.
 * Returns a Set of listing IDs for O(1) lookup.
 *
 * @example
 * const { isFavorited, setFavorited, isLoading } = useFavoritedListingIds();
 *
 * // Check if a listing is favorited
 * const favorited = isFavorited(listing.id);
 *
 * // Update after a mutation (optimistic update)
 * setFavorited(listing.id, true);
 */
export function useFavoritedListingIds() {
  const $ = _c(33);
  const {
    data: session,
    status
  } = useSession();
  const isAuthenticated = status === "authenticated" && session?.user != null;
  const userId = session?.user?.id ?? null;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      forUserId: null,
      overrides: new Map()
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [localOverridesState, setLocalOverridesState] = useState(t0);
  const hasFetchedRef = useRef(false);
  const previousUserIdRef = useRef(null);
  const userIdRef = useRef(userId);
  let t1;
  let t2;
  if ($[1] !== userId) {
    t1 = () => {
      userIdRef.current = userId;
    };
    t2 = [userId];
    $[1] = userId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== localOverridesState.forUserId || $[5] !== localOverridesState.overrides || $[6] !== userId) {
    t3 = localOverridesState.forUserId === userId ? localOverridesState.overrides : new Map();
    $[4] = localOverridesState.forUserId;
    $[5] = localOverridesState.overrides;
    $[6] = userId;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const localOverrides = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      fetchPolicy: "network-only"
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const [fetchFavorites, t5] = useLazyQuery(ListingsListAllFavoritedDocument, t4);
  const {
    loading,
    data,
    called
  } = t5;
  let t6;
  let t7;
  if ($[9] !== isAuthenticated || $[10] !== userId) {
    t6 = () => {
      const userChanged = previousUserIdRef.current !== userId;
      const wasAuthenticated = previousUserIdRef.current !== null;
      const isNowUnauthenticated = !isAuthenticated;
      if (userChanged && (wasAuthenticated || isNowUnauthenticated)) {
        hasFetchedRef.current = false;
      }
      previousUserIdRef.current = userId;
    };
    t7 = [isAuthenticated, userId];
    $[9] = isAuthenticated;
    $[10] = userId;
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useEffect(t6, t7);
  let t8;
  if ($[13] !== fetchFavorites || $[14] !== isAuthenticated) {
    t8 = () => {
      if (isAuthenticated && !hasFetchedRef.current) {
        hasFetchedRef.current = true;
        fetchFavorites({
          variables: {
            page: 1,
            pageSize: 500,
            sort: ListingFavoriteSortOptions.FavoritedAtNewestFirst
          }
        });
      }
    };
    $[13] = fetchFavorites;
    $[14] = isAuthenticated;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== fetchFavorites || $[17] !== isAuthenticated || $[18] !== userId) {
    t9 = [isAuthenticated, fetchFavorites, userId];
    $[16] = fetchFavorites;
    $[17] = isAuthenticated;
    $[18] = userId;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  useEffect(t8, t9);
  let t10;
  bb0: {
    if (data?.listingsListAllFavorited?.__typename === "ListingCollection") {
      let t11;
      if ($[20] !== data.listingsListAllFavorited.entries) {
        t11 = new Set(data.listingsListAllFavorited.entries.map(_temp));
        $[20] = data.listingsListAllFavorited.entries;
        $[21] = t11;
      } else {
        t11 = $[21];
      }
      t10 = t11;
      break bb0;
    }
    let t11;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = new Set();
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    t10 = t11;
  }
  const serverFavoritedIds = t10;
  let combined;
  if ($[23] !== localOverrides || $[24] !== serverFavoritedIds) {
    combined = new Set(serverFavoritedIds);
    localOverrides.forEach((isFavorited, listingId) => {
      if (isFavorited) {
        combined.add(listingId);
      } else {
        combined.delete(listingId);
      }
    });
    $[23] = localOverrides;
    $[24] = serverFavoritedIds;
    $[25] = combined;
  } else {
    combined = $[25];
  }
  const favoritedIds = combined;
  let t11;
  if ($[26] !== favoritedIds) {
    t11 = listingId_0 => favoritedIds.has(listingId_0);
    $[26] = favoritedIds;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const isFavorited_0 = t11;
  let t12;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = (listingId_1, favorited) => {
      setLocalOverridesState(prev => {
        const next = new Map(prev.overrides);
        next.set(listingId_1, favorited);
        return {
          forUserId: userIdRef.current,
          overrides: next
        };
      });
    };
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const setFavorited = t12;
  const isLoading = loading || isAuthenticated && !called;
  let t13;
  if ($[29] !== favoritedIds || $[30] !== isFavorited_0 || $[31] !== isLoading) {
    t13 = {
      favoritedIds,
      isLoading,
      isFavorited: isFavorited_0,
      setFavorited
    };
    $[29] = favoritedIds;
    $[30] = isFavorited_0;
    $[31] = isLoading;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  return t13;
}
function _temp(entry) {
  return entry.id;
}